import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { getRoomName } from '../../../base/conference/functions';
import i18next from '../../../base/i18n/i18next';
import Icon from '../../../base/icons/components/Icon';
import { IconVolumeOff, IconVolumeUp } from '../../../base/icons/svg';
import { audioAutoplayUnblocked, isAudioAutoplayBlocked, resumeRoomAudio } from '../../../base/media/autoplay';
import { emitMobileTelemetryEvent } from '../../../mobile/navigation/telemetry';
/**
 * "Call sound is off": shown while the browser refuses to start the call's
 * audio (base/media/autoplay.ts), so a blocked room is never silently mute.
 *
 * - The people who need the sound to do the call (an interpreter, a
 *   captioner, the hearing party joining as a guest) get a decision over the
 *   room: "Turn on sound" or "Continue without sound". Everyone else (a Deaf
 *   client, an observer) keeps the room as it is, with a small "Sound off"
 *   control; nothing covers the consumer's video for them.
 * - Every control plays every call-audio element inside the person's click
 *   and hides only when all of them play. If the browser still refuses, it
 *   stays and says "Sound still blocked". A play() that settles after the
 *   attempt timed out still counts: if the sound starts, the room says so.
 * - A press on the decision's backdrop turns the sound on too, from the
 *   backdrop's own click. Resuming on the pointer event before it would
 *   remove the backdrop before iOS delivers that click, and the click would
 *   land on the room control underneath (hang-up, camera).
 * - Without the decision on screen, the first press or key anywhere in the
 *   room also resumes the sound, so a person who never looks at the control
 *   is not left mute. With it, only keys pressed outside it do.
 * - "Continue without sound" holds for the rest of the page: a layout
 *   remount shows "Sound off", not the decision again. It stops the gesture
 *   resume, and "Sound off" turns the sound on in one press.
 * - The decision is a plain dialog, not DialogFocus: react-focus-on hides
 *   everything else from screen readers and pointers, which silenced the
 *   live region and froze the call-waiting and incoming-call overlays above
 *   it. Focus starts on "Turn on sound", Tab stays on the two buttons,
 *   Escape continues without sound, and focus goes back where it was.
 * - Screen readers hear the block: the live region is mounted empty from the
 *   start and filled when the block happens (a region inserted together with
 *   its text is often not announced).
 */
function tr(key) {
    return String(i18next.t(key));
}
// One report per page: every remote audio element fails at about the same
// moment, and a reload is a new page with its own chance to be blocked.
let blockReported = false;
// "Continue without sound" holds for the page, across layout remounts.
let continuedWithoutSound = false;
/** Test seam: a fresh page. */
export function resetAudioAutoplayPromptForTests() {
    blockReported = false;
    continuedWithoutSound = false;
}
// The roles that need the call's sound to do the call get the decision.
const SOUND_ROLES = new Set(['captioner', 'guest', 'interpreter']);
// A press or key on these (the decision, the Sound off control) is answered
// by that control, not by the room-wide gesture resume.
const CONTROL_ATTRIBUTE = 'data-audio-autoplay-control';
// Gestures that grant the page user activation: a mouse press or key down,
// and the release of a touch or pen (a touch press alone grants none).
const GESTURE_EVENTS = ['pointerdown', 'pointerup', 'keydown'];
// With the decision up, a press lands on its backdrop, which answers in its
// own click; only a key pressed outside it (focus in an overlay) is left.
const KEY_EVENTS = ['keydown'];
function isAutoplayControl(target) {
    const element = target;
    return typeof element?.closest === 'function' && Boolean(element.closest(`[${CONTROL_ATTRIBUTE}]`));
}
/**
 * Reports how an attempt to turn the sound on went.
 *
 * @param {IResumeContext} context - Who asked, in which room.
 * @param {ResumeTrigger} trigger - What the person pressed.
 * @param {IResumeAudioResult} result - What the browser did.
 * @param {boolean} late - Whether this is the outcome of an attempt that already timed out.
 * @returns {void}
 */
function reportResume({ role, roomName }, trigger, result, late = false) {
    emitMobileTelemetryEvent('vrs_room_audio_autoplay_resume', {
        elements: result.played,
        error: result.error,
        ...late ? { late } : {},
        ok: result.ok,
        role,
        trigger
    }, { roomName });
}
/**
 * An attempt that timed out reported a refusal; if its play() calls settle
 * after all and the sound plays, the room's state follows the sound.
 *
 * @param {IResumeContext} context - Who asked, in which room.
 * @param {ResumeTrigger} trigger - What the person pressed.
 * @param {IResumeAudioResult} result - The attempt, with `late` when it timed out.
 * @returns {void}
 */
function followLateResult(context, trigger, result) {
    result.late?.then(final => {
        reportResume(context, trigger, final, true);
        if (final.ok) {
            context.dispatch(audioAutoplayUnblocked());
        }
    });
}
const useStyles = makeStyles()(theme => ({
    backdrop: {
        alignItems: 'flex-end',
        background: 'rgba(2, 6, 12, 0.4)',
        boxSizing: 'border-box',
        display: 'flex',
        inset: 0,
        justifyContent: 'center',
        padding: theme.spacing(2, 2, 12),
        position: 'fixed',
        zIndex: 1100
    },
    liveRegion: {
        border: 0,
        clip: 'rect(0 0 0 0)',
        height: 1,
        margin: -1,
        overflow: 'hidden',
        padding: 0,
        position: 'absolute',
        whiteSpace: 'nowrap',
        width: 1
    },
    prompt: {
        alignItems: 'center',
        background: '#101827',
        border: '2px solid rgba(140, 200, 255, 0.72)',
        borderRadius: 18,
        boxShadow: '0 20px 64px rgba(0, 0, 0, 0.58)',
        boxSizing: 'border-box',
        color: '#F5F8FB',
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
        maxWidth: 440,
        padding: theme.spacing(3),
        textAlign: 'center',
        width: '100%'
    },
    heading: {
        color: '#FFFFFF',
        fontSize: 22,
        fontWeight: 800,
        lineHeight: 1.2,
        margin: 0
    },
    actions: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 12,
        justifyContent: 'center',
        width: '100%',
        '@media (max-width: 480px)': {
            flexDirection: 'column'
        }
    },
    turnOn: {
        alignItems: 'center',
        background: '#FFFFFF',
        border: 0,
        borderRadius: 999,
        color: '#0B1220',
        cursor: 'pointer',
        display: 'inline-flex',
        font: 'inherit',
        fontSize: 16,
        fontWeight: 800,
        gap: 6,
        justifyContent: 'center',
        minHeight: 48,
        padding: '4px 18px',
        '@media (max-width: 480px)': {
            width: '100%'
        },
        '&:disabled': {
            cursor: 'progress',
            opacity: 0.7
        },
        '&:focus-visible': {
            outline: '3px solid #8CC8FF',
            outlineOffset: 2
        }
    },
    icon: {
        display: 'inline-flex'
    },
    soundOff: {
        alignItems: 'center',
        background: 'rgba(8, 12, 20, 0.82)',
        border: '1px solid rgba(255, 255, 255, 0.3)',
        borderRadius: 999,
        color: '#F5F8FB',
        cursor: 'pointer',
        display: 'inline-flex',
        font: 'inherit',
        fontSize: 13,
        fontWeight: 600,
        gap: 4,
        left: '50%',
        minHeight: 44,
        padding: '4px 10px',
        position: 'absolute',
        top: 12,
        transform: 'translateX(-50%)',
        zIndex: 1100,
        '&:disabled': {
            cursor: 'progress',
            opacity: 0.7
        },
        '&:focus-visible': {
            outline: '3px solid #8CC8FF',
            outlineOffset: 2
        }
    },
    notNow: {
        background: 'transparent',
        border: '1px solid rgba(255, 255, 255, 0.35)',
        borderRadius: 999,
        color: '#F5F8FB',
        cursor: 'pointer',
        font: 'inherit',
        fontSize: 14,
        minHeight: 44,
        padding: '6px 12px',
        '@media (max-width: 480px)': {
            width: '100%'
        },
        '&:focus-visible': {
            outline: '3px solid #8CC8FF',
            outlineOffset: 2
        }
    }
}));
const AudioAutoplayPrompt = ({ role }) => {
    const { classes } = useStyles();
    const dispatch = useDispatch();
    const blocked = useSelector((state) => isAudioAutoplayBlocked(state));
    const roomName = useSelector((state) => getRoomName(state));
    const [status, setStatus] = useState('idle');
    const [withoutSound, setWithoutSound] = useState(continuedWithoutSound);
    const turnOnButton = useRef(null);
    const notNowButton = useRef(null);
    const decision = blocked && !withoutSound && SOUND_ROLES.has(role);
    const listening = blocked && !withoutSound;
    useEffect(() => {
        if (!blocked || blockReported) {
            return;
        }
        blockReported = true;
        emitMobileTelemetryEvent('vrs_room_audio_autoplay_blocked', { role }, { roomName });
    }, [blocked, role, roomName]);
    // A later block starts from "Call sound is off", not an old failure.
    useEffect(() => {
        if (!blocked) {
            setStatus('idle');
        }
    }, [blocked]);
    // The room-wide resume: the next press or key anywhere else plays the
    // room's audio inside that gesture. Listening in the capture phase, so
    // nothing in the room can swallow it first.
    useEffect(() => {
        if (!listening || typeof document === 'undefined' || typeof document.addEventListener !== 'function') {
            return undefined;
        }
        const context = {
            dispatch,
            role,
            roomName
        };
        const events = decision ? KEY_EVENTS : GESTURE_EVENTS;
        let attempting = false;
        let failureReported = false;
        const onGesture = (event) => {
            // Escape grants no user activation, so it could only fail.
            if (attempting || isAutoplayControl(event.target) || ('key' in event && event.key === 'Escape')) {
                return;
            }
            attempting = true;
            // Synchronous inside the gesture: every play() carries it.
            resumeRoomAudio(document).then(result => {
                attempting = false;
                if (result.ok || !failureReported) {
                    failureReported = failureReported || !result.ok;
                    reportResume(context, 'gesture', result);
                }
                followLateResult(context, 'gesture', result);
                if (result.ok) {
                    dispatch(audioAutoplayUnblocked());
                }
            });
        };
        events.forEach(type => document.addEventListener(type, onGesture, true));
        return () => events.forEach(type => document.removeEventListener(type, onGesture, true));
    }, [decision, dispatch, listening, role, roomName]);
    // The decision takes focus, and gives it back where it was when it goes.
    useEffect(() => {
        if (!decision || typeof document === 'undefined') {
            return undefined;
        }
        const previous = document.activeElement;
        turnOnButton.current?.focus();
        return () => {
            const active = document.activeElement;
            if (previous && previous !== document.body && previous.isConnected
                && (!active || active === document.body) && typeof previous.focus === 'function') {
                previous.focus({ preventScroll: true });
            }
        };
    }, [decision]);
    const heading = tr(status === 'failed' ? 'vrs.layout.sound.stillBlocked' : 'vrs.layout.sound.heading');
    // Mounted before any block, so filling it is announced. After "Continue
    // without sound" it speaks again only if Sound off fails.
    const liveRegion = (React.createElement("div", { "aria-live": 'polite', className: classes.liveRegion, "data-testid": 'vrs-audio-autoplay-announcement', role: 'status' }, blocked && (status === 'failed' || !withoutSound) ? heading : ''));
    if (!blocked) {
        return liveRegion;
    }
    const turnOn = (trigger) => {
        if (status === 'pending') {
            return;
        }
        const context = {
            dispatch,
            role,
            roomName
        };
        setStatus('pending');
        // Synchronous inside the click: every play() carries the gesture.
        resumeRoomAudio(document).then(result => {
            reportResume(context, trigger, result);
            followLateResult(context, trigger, result);
            if (result.ok) {
                setStatus('idle');
                dispatch(audioAutoplayUnblocked());
            }
            else {
                setStatus('failed');
            }
        });
    };
    const continueWithoutSound = () => {
        continuedWithoutSound = true;
        setWithoutSound(true);
        setStatus('idle');
    };
    if (!decision) {
        return (React.createElement(React.Fragment, null,
            liveRegion,
            React.createElement("button", { className: classes.soundOff, "data-audio-autoplay-control": 'true', "data-testid": 'vrs-audio-sound-off', disabled: status === 'pending', onClick: () => turnOn('control'), type: 'button' },
                React.createElement("span", { "aria-hidden": 'true', className: classes.icon },
                    React.createElement(Icon, { color: '#F5F8FB', size: 16, src: IconVolumeOff })),
                status === 'failed' ? heading : tr('vrs.layout.sound.soundOff'))));
    }
    // Tab stays on the two buttons; Escape continues without sound.
    const onDecisionKeyDown = (event) => {
        if (event.key === 'Escape') {
            event.preventDefault();
            continueWithoutSound();
            return;
        }
        const first = turnOnButton.current;
        const last = notNowButton.current;
        if (event.key !== 'Tab' || !first || !last) {
            return;
        }
        if (event.shiftKey ? document.activeElement === first : document.activeElement === last) {
            event.preventDefault();
            (event.shiftKey ? last : first).focus();
        }
    };
    return (React.createElement(React.Fragment, null,
        liveRegion,
        React.createElement("div", { className: classes.backdrop, "data-testid": 'vrs-audio-autoplay-backdrop', onClick: event => {
                // The backdrop itself, not the decision on it.
                if (event.target === event.currentTarget) {
                    turnOn('backdrop');
                }
            } },
            React.createElement("div", { "aria-labelledby": 'vrs-audio-autoplay-title', className: classes.prompt, "data-audio-autoplay-control": 'true', "data-testid": 'vrs-audio-autoplay-prompt', onKeyDown: onDecisionKeyDown, role: 'dialog' },
                React.createElement("h2", { className: classes.heading, id: 'vrs-audio-autoplay-title' }, heading),
                React.createElement("div", { className: classes.actions },
                    React.createElement("button", { className: classes.turnOn, disabled: status === 'pending', onClick: () => turnOn('button'), ref: turnOnButton, type: 'button' },
                        React.createElement("span", { "aria-hidden": 'true', className: classes.icon },
                            React.createElement(Icon, { color: '#0B1220', size: 20, src: IconVolumeUp })),
                        tr('vrs.layout.sound.turnOn')),
                    React.createElement("button", { className: classes.notNow, onClick: continueWithoutSound, ref: notNowButton, type: 'button' }, tr('vrs.layout.sound.notNow')))))));
};
export default AudioAutoplayPrompt;
